/**
 * Groups elements from an iterable into an object based on a callback function.
 *
 * @template T, K
 * @param {Iterable<T>} iterable - The iterable to group.
 * @param {function(T, number): K} callback - The callback function to
 * determine the grouping key.
 * @returns {Object.<string, T[]>} An object where keys are the grouping keys
 * and values are arrays of grouped elements.
 */
if (!Object.groupBy) {
    Object.defineProperty(Object, 'groupBy', {
        value: function groupBy(iterable, callback) {
            const obj = Object.create(null);
            let i = 0;
            for (const value of iterable) {
                const key = callback(value, i++);
                if (obj[key]) {
                    obj[key].push(value);
                } else {
                    obj[key] = [value];
                }
            }
            return obj;
        },
        writable: true,
        configurable: true,
    });
}

/**
 * Groups elements from an iterable into a Map based on a callback function.
 *
 * @template K, T
 * @param {Iterable<T>} iterable - The iterable to group.
 * @param {function(T, number): K} callback - The callback function to
 * determine the grouping key.
 * @returns {Map<K, T[]>} A Map where keys are the grouping keys and values are
 * arrays of grouped elements.
 */
if (!Map.groupBy) {
    Object.defineProperty(Map, 'groupBy', {
        value: function groupBy(iterable, callback) {
            const map = new Map();
            let i = 0;
            for (const value of iterable) {
                const key = callback(value, i++);
                if (map.has(key)) {
                    map.get(key).push(value);
                } else {
                    map.set(key, [value]);
                }
            }
            return map;
        },
        writable: true,
        configurable: true,
    });
}
